import React from 'react';
import {createRoot, hydrateRoot} from 'react-dom/client';
import * as Sentry from '@sentry/react';
import * as serviceWorker from './serviceWorker';
import {BrowserRouter} from 'react-router-dom';
import {HelmetProvider} from 'react-helmet-async';
import {LastLocationProvider} from './components/hooks/useLastLocation';
import GoTopButton from './components/gotopbtn';
import App from './App';
import './assets/styles';
import './index.css';
import webUtils from './utils/webUtils';
import deviceUtils from './utils/deviceUtils';
import i18n from './utils/i18n';

if (process.env.REACT_APP_SENTRY_DSN && !deviceUtils.isPrerenderCrawler()) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    sendDefaultPii: false,
    sampleRate: 0.1,
  });
}

const rootElement = document.getElementById('root');
const isSdkPrivacy = window.location.href.indexOf('business/SDK/privacy') > -1;

const mount = () => {
  if (webUtils.isPrerenderHydration()) {
    console.log('[react hydrate]');
    hydrateRoot(
      rootElement,
      <HelmetProvider>
        <BrowserRouter>
          <LastLocationProvider>
            <App />
            <GoTopButton />
          </LastLocationProvider>
        </BrowserRouter>
      </HelmetProvider>
    );
  } else {
    console.log(
      `[react render] prerender-based: ${rootElement.hasChildNodes()}`
    );
    createRoot(rootElement).render(
      <HelmetProvider>
        <BrowserRouter>
          <LastLocationProvider>
            <App />
            {!isSdkPrivacy && <GoTopButton />}
          </LastLocationProvider>
        </BrowserRouter>
      </HelmetProvider>
    );
  }
};

let mounted = false;
const mountOnce = () => {
  if (mounted) return;
  mounted = true;
  mount();
};

if (deviceUtils.isPrerenderCrawler() || webUtils.isEn(i18n.language)) {
  mountOnce();
} else {
  const TRANSLATION_LOAD_TIMEOUT = 1500;

  Promise.race([
    i18n.loadNamespaces(i18n.options.ns),
    new Promise(resolve => setTimeout(resolve, TRANSLATION_LOAD_TIMEOUT)),
  ])
    .catch(
      err =>
        process.env.NODE_ENV !== 'production' &&
        console.warn('[i18n] preload namespaces failed', err)
    )
    .then(mountOnce);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
